import { ChevronRight } from "@mui/icons-material";
import { Box, Divider, Link, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import { useTranslation } from "react-i18next";
import StopIcon from "../../../../../assets/images/icons/StopIcon.tsx";
import StoppableButton from "../../../../../components/common/StoppableButton/StoppableButton.tsx";
import { APPS } from "../../../../../constants/AppsConstants.ts";
import { MAX_ALLOWED_SELECTED_IMAGES } from "../../../../../constants/ImageToImageConstants.ts";
import LinkConstants from "../../../../../constants/LinkConstants.ts";
import { useImageToImageConfig } from "../../../../../hooks/useImageToImageConfig.tsx";
import { useAppStore } from "../../../../../hooks/useStores.tsx";
import ExpandableMenuButton from "../../../../../layout/ExpandableMenu/Button/ExpandableMenuButton.tsx";
import ExpandableMenuLayout from "../../../../../layout/ExpandableMenu/Menu/ExpandableMenuLayout.tsx";
import {
	ImageEditingConfigsProps,
	ImageEditingViewMode,
	ImageToImageConfigType,
} from "../../../../../models/image-to-image.ts";
import { isPlaygroundLoadingImages } from "../../../../../utils/index.ts";
import iframeStore from "../../../../IframeNew/iframe-store.tsx";
import ImageToImageError from "../../Error/ImageToImageError.tsx";
import ConfigButtonLabel from "../ConfigButtonLabel/ConfigButtonLabel.tsx";
import ConfigDisplay from "../ConfigDisplay/ConfigDisplay";
import BackgroundConfig from "../Features/Background/BackgroundConfig";
import EraseObject from "../Features/EraseObject/EraseObject.tsx";
import ForegroundPlacement from "../Features/ForegroundPlacement/ForegroundPlacement";
import ManualPlacement from "../Features/ManualPlacement/ManualPlacement.tsx";
import SizeConfig from "../Features/Size/SizeConfig";
import styles from "../ImageToImageConfig.module.scss";

const ImageEditingConfigs = observer(({ handleGenerateImageToImage }: ImageEditingConfigsProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "playground.imageToImage.config" });
	const { imageToImageStore, playgroundStore, tourStore } = useAppStore();
	const {
		isGenerateEnabled,
		updateSelectedBackgroundConfigs,
		updateSelectedSizeConfigs,
		updateSelectedForegroundPlacementConfigs,
	} = useImageToImageConfig();

	const [activeConfig, setActiveConfig] = useState<string>("");
	const [isSubMenuOpen, setIsSubMenuOpen] = useState<boolean>(false);
	const [isSingleMode, setIsSingleMode] = useState<boolean>(false);
	const menuRef = useRef<HTMLDivElement | null>(null);

	const hasLoadingImages = isPlaygroundLoadingImages(playgroundStore.playgroundResults, APPS.IMAGE_TO_IMAGE);
	const [isErrorExistInSelectedImages, setIsErrorExistInSelectedImages] = useState<boolean>(false);
	const [errorPositionY, setErrorPositionY] = useState<number>(0);

	useEffect(() => {
		setIsErrorExistInSelectedImages(playgroundStore.getSelectedImages().some((image) => image.error !== undefined));
	}, [playgroundStore.getSelectedImages()]);

	useEffect(() => {
		setIsSingleMode(playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE);
	}, [playgroundStore.imageEditingViewMode]);

	useEffect(() => {
		const el = menuRef.current;
		if (!el) return;

		const handleScroll = () => {
			setErrorPositionY(el.scrollTop);
		};

		el.addEventListener("scroll", handleScroll);

		return () => {
			el.removeEventListener("scroll", handleScroll);
		};
	}, [menuRef]);

	const handleDeleteConfig = (configKey: keyof ImageToImageConfigType, field: string, value: any) => {
		const currentConfig = { ...(imageToImageStore.config[configKey] as Record<string, any> | undefined) };

		imageToImageStore.handleConfigChange(configKey, {
			...currentConfig,
			[field]: value,
		});
	};

	const onSubMenuOpen = (open: boolean) => {
		setIsSubMenuOpen(open);
	};

	const ToolTipInfo = ({ tooltip, href }: { tooltip: string; href?: string }) => (
		<>
			{tooltip}
			{!iframeStore.isIframe() && href && (
				<Box className={styles.apiDocContainer}>
					<Link href={href} target={"_blank"} className={styles.link}>
						{t("apiDocumentation")} <ChevronRight />
					</Link>
				</Box>
			)}
		</>
	);
	return (
		<Box className={styles.imageToImageConfigsWrapper}>
			<ExpandableMenuLayout
				className={clsx(styles.config, styles.imageToImageConfigs)}
				setActiveConfig={setActiveConfig}
				isSubMenuOpen={isSubMenuOpen}
				ref={menuRef}
				hideSubPanel={
					(isErrorExistInSelectedImages && isSingleMode) || tourStore.activeTour?.name === "feedback-reminder"
				}
			>
				<Box className={styles.configHeader}>
					<Typography variant="h6" className={styles.title}>
						{isSingleMode ? t("singleModeTitle") : t("bulkModeTitle")}
					</Typography>
					<Typography className={styles.subTitle}>
						{isSingleMode ? t("singleModeSubTitle") : t("bulkModeSubTitle")}
					</Typography>
				</Box>
				{(!iframeStore.isIframe() || iframeStore.iframe.config.image_editing_config?.available_tabs?.size) && (
					<ExpandableMenuButton
						expandTo={<SizeConfig />}
						subMenuPanelProps={{ title: t("features.size.title") }}
						isActive={activeConfig === t("features.size.title")}
						hidden={isSingleMode}
						disabled={isErrorExistInSelectedImages}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.size.button")}
							info={renderToString(
								<ToolTipInfo
									tooltip={t("features.size.tooltip")}
									href={LinkConstants.BRIA_API_IMAGE_EXPANSTION}
								/>,
							)}
						/>
					</ExpandableMenuButton>
				)}

				{!isSingleMode && (
					<ConfigDisplay
						config={imageToImageStore.config.size}
						onDelete={(configKey: string, value: string) => {
							handleDeleteConfig("size", configKey, value);
							updateSelectedSizeConfigs();
						}}
						expandTo={<SizeConfig />}
						subMenuPanelProps={{ title: t("features.size.title") }}
					/>
				)}
				{(!iframeStore.isIframe() || iframeStore.iframe.config.image_editing_config?.available_tabs?.size) && (
					<Divider />
				)}

				{(!iframeStore.isIframe() ||
					iframeStore.iframe.config.image_editing_config?.available_tabs?.size_and_placement) && (
					<ExpandableMenuButton
						expandTo={<ManualPlacement />}
						subMenuPanelProps={{
							title: t("features.manualPlacement.title"),
							subTitle: t("features.manualPlacement.subTitle"),
						}}
						isActive={activeConfig === t("features.manualPlacement.title")}
						hidden={!isSingleMode}
						disabled={isErrorExistInSelectedImages}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.manualPlacement.button")}
							info={t("features.manualPlacement.tooltip")}
						/>
					</ExpandableMenuButton>
				)}
				{isSingleMode && <Divider />}

				{(!iframeStore.isIframe() ||
					(isSingleMode &&
						iframeStore.iframe.config.image_editing_config?.available_tabs?.single_background) ||
					(!isSingleMode &&
						iframeStore.iframe.config.image_editing_config?.available_tabs?.bulk_background)) && (
					<ExpandableMenuButton
						expandTo={<BackgroundConfig onSubMenuOpen={onSubMenuOpen} />}
						subMenuPanelProps={{
							title: t("features.background.title"),
							subTitle: t("features.background.subTitle"),
						}}
						isActive={activeConfig === t("features.background.title")}
						disabled={isErrorExistInSelectedImages}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.background.button")}
							info={renderToString(
								<ToolTipInfo
									tooltip={t("features.background.tooltip")}
									href={LinkConstants.BRIA_API_BACKGROUND_REMOVE}
								/>,
							)}
						/>
					</ExpandableMenuButton>
				)}
				{!isSingleMode && (
					<ConfigDisplay
						config={imageToImageStore.config.background}
						onDelete={(configKey: string, value: string) => {
							handleDeleteConfig("background", configKey, value);
							updateSelectedBackgroundConfigs();
						}}
						expandTo={<BackgroundConfig onSubMenuOpen={onSubMenuOpen} />}
						subMenuPanelProps={{ title: t("features.background.title") }}
					/>
				)}
				{(!iframeStore.isIframe() ||
					(isSingleMode &&
						iframeStore.iframe.config.image_editing_config?.available_tabs?.single_background) ||
					(!isSingleMode &&
						iframeStore.iframe.config.image_editing_config?.available_tabs?.bulk_background)) && (
					<Divider />
				)}

				{(!iframeStore.isIframe() ||
					iframeStore.iframe.config.image_editing_config?.available_tabs?.foreground_placement) && (
					<ExpandableMenuButton
						expandTo={<ForegroundPlacement />}
						subMenuPanelProps={{ title: t("features.foregroundPlacement.title") }}
						isActive={activeConfig === t("features.foregroundPlacement.title")}
						hidden={isSingleMode}
						disabled={isErrorExistInSelectedImages}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.foregroundPlacement.button")}
							info={renderToString(<ToolTipInfo tooltip={t("features.foregroundPlacement.tooltip")} />)}
						/>
					</ExpandableMenuButton>
				)}
				{!isSingleMode && (
					<ConfigDisplay
						config={imageToImageStore.config.foreground_placement}
						onDelete={(configKey: string, value: string) => {
							handleDeleteConfig("foreground_placement", configKey, value);
							updateSelectedForegroundPlacementConfigs();
						}}
						expandTo={<ForegroundPlacement />}
						subMenuPanelProps={{ title: t("features.foregroundPlacement.title") }}
					/>
				)}

				{(!iframeStore.isIframe() ||
					iframeStore.iframe.config.image_editing_config?.available_tabs?.objects) && (
					<ExpandableMenuButton
						expandTo={<EraseObject />}
						subMenuPanelProps={{
							title: t("features.eraseObject.title"),
							subTitle: t("features.eraseObject.subTitle"),
						}}
						isActive={activeConfig === t("features.eraseObject.title")}
						hidden={!isSingleMode}
						disabled={isErrorExistInSelectedImages}
					>
						<ConfigButtonLabel
							className={clsx(styles.buttonLabel)}
							label={t("features.eraseObject.button")}
							info={renderToString(
								<ToolTipInfo
									tooltip={t("features.eraseObject.tooltip")}
									href={LinkConstants.BRIA_API_REMOVE_OBJECT}
								/>,
							)}
						/>
					</ExpandableMenuButton>
				)}
				<Box
					className={styles.imageToImageErrorContainer}
					sx={{
						bottom: -errorPositionY + 20,
						left: "20px",
						right: "20px",
					}}
				>
					<ImageToImageError />
				</Box>
			</ExpandableMenuLayout>
			{!isSingleMode && (
				<StoppableButton
					condition={
						hasLoadingImages &&
						(!imageToImageStore.editingCanvasStore.isEraseConfigsEnabled ||
							!imageToImageStore.isGeneratingImages)
					}
					onStopButtonClick={() => imageToImageStore.abortImageGeneration()}
					onButtonClick={handleGenerateImageToImage}
					disabled={!isGenerateEnabled() || isErrorExistInSelectedImages}
					buttonLabel={
						<>
							{imageToImageStore.getGeneratedImagesCount > MAX_ALLOWED_SELECTED_IMAGES
								? t("tooManySelected")
								: t("button")}
							{imageToImageStore.getGeneratedImagesCount > 0 &&
								isGenerateEnabled() &&
								` ${imageToImageStore.getGeneratedImagesCount} ${t("results")}`}
						</>
					}
					stopButtonLabel={
						<>
							<StopIcon /> {t("stopGenerating")}
						</>
					}
					fullWidth
					className={`generate-image-to-image ${styles.stopGeneratingBt}`}
				/>
			)}
		</Box>
	);
});

export default ImageEditingConfigs;
